import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Operadores do app (UC13).
 *
 * Não confundir com o usuário do painel: o operador entra no coletor com
 * matrícula e PIN, por um dispositivo já pareado. `bloqueado` é o freio de
 * força bruta — derivado do relógio, não decisão de ninguém.
 */

const CAMINHO_COLECAO = montarCaminho('/operadores');

export const operadorSchema = z.object({
  id: z.string(),
  matricula: z.string(),
  nome: z.string(),
  ativo: z.boolean(),
  /** Sob bloqueio de força bruta agora — derivado de `bloqueadoAte`. */
  bloqueado: z.boolean(),
  bloqueadoAte: z.string().nullable(),
  /** Falhas de PIN desde o último acerto. */
  tentativasFalhas: z.number().int().nonnegative(),
  ultimoAcessoEm: z.string().nullable(),
  criadoEm: z.string(),
  alteradoEm: z.string(),
});

export type Operador = z.infer<typeof operadorSchema>;

const paginaDeOperadoresSchema = paginaSchema(operadorSchema);

/** `Bloqueado` não é coluna: sai da comparação de `bloqueadoAte` com o agora. */
export type SituacaoOperador = 'Ativo' | 'Inativo' | 'Bloqueado';

export type FiltroDeOperadores = ParametrosDePagina & {
  /** Trecho de matrícula ou de nome, sem depender da caixa. */
  readonly busca?: string;
  readonly situacao?: SituacaoOperador;
};

export type CadastroDeOperador = {
  readonly matricula: string;
  readonly nome: string;
  /** Numérico, 4 a 8 dígitos. Vai só no corpo. */
  readonly pin: string;
};

export type AlteracaoDeOperador = {
  readonly matricula?: string;
  readonly nome?: string;
  /** Desativar encerra as sessões de app do operador na hora. */
  readonly ativo?: boolean;
};

export async function listarOperadores(filtro: FiltroDeOperadores = {}): Promise<Pagina<Operador>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: { ...parametrosDePagina(filtro), busca: filtro.busca, situacao: filtro.situacao },
  });

  return analisar(paginaDeOperadoresSchema, corpo, CAMINHO_COLECAO);
}

export async function obterOperador(id: string): Promise<Operador> {
  const caminho = montarCaminho('/operadores/{id}', { id });
  return analisar(operadorSchema, await requisitar(caminho), caminho);
}

export async function criarOperador(cadastro: CadastroDeOperador): Promise<Operador> {
  const corpo = await requisitar(CAMINHO_COLECAO, { metodo: 'POST', corpo: cadastro });
  return analisar(operadorSchema, corpo, CAMINHO_COLECAO);
}

export async function atualizarOperador(
  id: string,
  mudancas: AlteracaoDeOperador,
): Promise<Operador> {
  const caminho = montarCaminho('/operadores/{id}', { id });
  const corpo = await requisitar(caminho, { metodo: 'PATCH', corpo: mudancas });
  return analisar(operadorSchema, corpo, caminho);
}

/** O PIN nunca vai na URL. */
export async function redefinirPin(id: string, dados: { readonly pin: string }): Promise<Operador> {
  const caminho = montarCaminho('/operadores/{id}/pin', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST', corpo: dados });
  return analisar(operadorSchema, corpo, caminho);
}

/** Solta o freio de força bruta antes de `bloqueadoAte`. Decisão humana. */
export async function desbloquearOperador(id: string): Promise<Operador> {
  const caminho = montarCaminho('/operadores/{id}/desbloquear', { id });
  const corpo = await requisitar(caminho, { metodo: 'POST' });
  return analisar(operadorSchema, corpo, caminho);
}
